import type { SavedReply, ThreadTopic } from '@zuruflex/types';
import { MessageSquareQuote } from 'lucide-react';
import { Button } from '../ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';

/**
 * Untranslated copy, handed in.
 *
 * Saved replies are a staff tool and their copy lives in the `admin`
 * namespace, which `@zuruflex/ui` must not reach into — a component in the
 * design system that reads a namespace only one app loads would render
 * raw key names anywhere else. So the strings arrive as props, the way
 * `RatingInput.labels` does.
 */
export interface SavedReplyPickerLabels {
  /** Names the trigger — "Saved replies". */
  trigger: string;
  /** Shown when the list is empty — "No saved replies yet". */
  empty: string;
}

export interface SavedReplyPickerProps {
  replies: SavedReply[];
  onInsert: (reply: SavedReply) => void;
  labels: SavedReplyPickerLabels;
  /**
   * The thread's own language and subject. Used to *order* the list, never
   * to cut it down: an agent answering a French thread in English because
   * that is what the customer switched to should not have to go and find
   * the reply somewhere else.
   */
  locale?: string;
  topic?: ThreadTopic | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Sorts the ones that fit this thread to the top.
 *
 * Locale outranks topic, because the wrong language is unusable while the
 * wrong heading is merely untidy. Within a tier the API's own order is
 * kept, so a team that has arranged their replies keeps that arrangement.
 */
function rank(reply: SavedReply, locale?: string, topic?: ThreadTopic | null): number {
  const sameLocale = locale ? reply.locale === locale : true;
  const sameTopic = topic ? reply.topic === topic : reply.topic === null;
  return (sameLocale ? 0 : 2) + (sameTopic ? 0 : 1);
}

/**
 * The canned answers, one click from the composer.
 *
 * Inserting rather than sending: every one of these needs a name, a date
 * or an apology pasted into it before it is worth reading, and a picker
 * that sent on click would make the boilerplate the whole message.
 */
export function SavedReplyPicker({
  replies,
  onInsert,
  labels,
  locale,
  topic,
  disabled,
  className,
}: SavedReplyPickerProps) {
  const items = replies
    .filter((reply) => reply.isActive)
    .map((reply, index) => ({ reply, index, rank: rank(reply, locale, topic) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.reply);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={disabled}
          aria-label={labels.trigger}
          className={className}
        >
          <MessageSquareQuote className="size-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="max-h-80 w-80 overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-2.5 py-3 text-sm text-text-muted">{labels.empty}</p>
        ) : (
          items.map((reply) => (
            <DropdownMenuItem
              key={reply.id}
              onSelect={() => onInsert(reply)}
              className="flex-col items-start gap-0.5"
            >
              <span className="flex w-full items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-semibold">{reply.title}</span>
                {/*
                  The tag is the stored code — `fr`, `PAYMENT` — not a
                  translation of it. A two-letter language code is the one
                  label that reads the same in all three languages, and the
                  topic sits beside a heading the agent picked themselves.
                */}
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-subtle">
                  {[reply.locale, reply.topic].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="line-clamp-2 w-full text-xs text-text-muted">{reply.body}</span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
